/**
 * SQL 解析器 - 增强 Pro 版
 * 能够从原始 SQL 中提取表结构、字段属性、索引和外键。
 */
export function parseSchema(sql, dialect = 'mysql') {
  const ast = {};
  const cleanSql = sql
    .replace(/--.*$/gm, '')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\s+/g, ' ');

  // 分割多个 CREATE TABLE 语句
  const parts = cleanSql.split(/CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?/i);
  
  parts.slice(1).forEach(part => {
    const nameMatch = part.match(/^([^\s(]+)/);
    if (!nameMatch) return;
    
    const tableName = nameMatch[1].replace(/[`"]/g, '').split('.').pop();
    const body = extractParenthesisContent(part);
    if (!body) return;

    ast[tableName] = { columns: {}, indexes: {}, foreignKeys: {}, comment: '' };

    // 提取 MySQL 表注释
    const afterBody = part.substring(part.indexOf(body) + body.length);
    const tableCommentMatch = afterBody.match(/COMMENT\s*=\s*'([^']*)'/i);
    if (tableCommentMatch) ast[tableName].comment = tableCommentMatch[1];

    // 按逗号分割行，但忽略括号内的逗号 (如 DECIMAL(10,2))
    const lines = splitByComma(body);
    lines.forEach(line => {
      line = line.trim();
      if (!line) return;

      // 1. 外键解析
      const fkMatch = line.match(/(?:CONSTRAINT\s+([^\s]+)\s+)?FOREIGN\s+KEY\s*\(([^)]+)\)\s+REFERENCES\s+([^\s(]+)\s*\(([^)]+)\)/i);
      if (fkMatch) {
        const name = (fkMatch[1] || `fk_${Math.random().toString(36).slice(2, 7)}`).replace(/[`"]/g, '');
        ast[tableName].foreignKeys[name] = {
          column: fkMatch[2].replace(/[`"]/g, '').trim(),
          refTable: fkMatch[3].replace(/[`"]/g, '').trim(),
          refColumn: fkMatch[4].replace(/[`"]/g, '').trim()
        };
        return;
      }

      // 2. 索引解析
      const indexMatch = line.match(/^(?:CONSTRAINT\s+([^\s]+)\s+)?(PRIMARY\s+KEY|UNIQUE\s+KEY|KEY|INDEX)\s*(?:([^\s(]+))?\s*\(([^)]+)\)/i);
      if (indexMatch) {
        const type = indexMatch[2].toUpperCase();
        const name = (indexMatch[3] || indexMatch[1] || (type === 'PRIMARY KEY' ? 'PRIMARY' : `idx_${Math.random().toString(36).slice(2, 7)}`)).replace(/[`"]/g, '');
        ast[tableName].indexes[name] = {
          type,
          columns: indexMatch[4].replace(/[`"]/g, '').split(',').map(c => c.trim())
        };
        return;
      }

      // 3. 字段解析
      const colMatch = line.match(/^([^\s]+)\s+([a-zA-Z]+(?:\s*\([^)]+\))?)(.*)$/i);
      if (colMatch) {
        const colName = colMatch[1].replace(/[`"]/g, '');
        const colType = colMatch[2].toUpperCase();
        const rest = colMatch[3];
        if (/^(PRIMARY|UNIQUE|KEY|INDEX|CONSTRAINT|FOREIGN)$/i.test(colName)) return;

        ast[tableName].columns[colName] = {
          type: colType,
          nullable: !/NOT\s+NULL/i.test(rest),
          defaultValue: (rest.match(/DEFAULT\s+([^'\s]+|'[^']*')/i) || [])[1] || null,
          comment: (rest.match(/COMMENT\s+'([^']*)'/i) || [])[1] || '',
          autoIncrement: /AUTO_INCREMENT|SERIAL/i.test(rest)
        };
      }
    });
  });

  // 4. PostgreSQL 独立注释解析
  const commentRegex = /COMMENT\s+ON\s+COLUMN\s+([^\s]+)\s+IS\s+'([^']*)'/gi;
  let commentMatch;
  while ((commentMatch = commentRegex.exec(cleanSql)) !== null) {
    const fullPath = commentMatch[1].replace(/[`"]/g, '');
    const parts = fullPath.split('.');
    const colName = parts.pop();
    const tableName = parts.pop();
    if (tableName && ast[tableName] && ast[tableName].columns[colName]) {
      ast[tableName].columns[colName].comment = commentMatch[2];
    }
  }

  return ast;
}

/**
 * 辅助函数：提取第一个括号对内的内容
 */
function extractParenthesisContent(str) {
  let depth = 0;
  let startIdx = str.indexOf('(');
  if (startIdx === -1) return null;
  for (let i = startIdx; i < str.length; i++) {
    if (str[i] === '(') depth++;
    else if (str[i] === ')') depth--;
    if (depth === 0) return str.substring(startIdx + 1, i);
  }
  return null;
}

/**
 * 辅助函数：根据逗号分割字符串，但跳过括号内的逗号
 */
function splitByComma(str) {
  const result = [];
  let current = '', depth = 0;
  for (const char of str) {
    if (char === '(') depth++;
    else if (char === ')') depth--;
    if (char === ',' && depth === 0) {
      result.push(current);
      current = '';
    } else current += char;
  }
  if (current) result.push(current);
  return result;
}
